// Overview — scoped CSR account workspace (first tab: Overview).
// Internal tabs match the CSR Workspace mock but are NOT separate sidebar items.
// Comparison window: YTD full months vs same period last year.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import client from '../api/client';
import { useCache } from '../hooks/useCache';
import { useCustomerFilter } from '../hooks/useCustomerFilter';
import { useScopeSync } from '../hooks/useScopeSync';
import { useCurrentScope } from '../hooks/useCurrentScope';
import { useScope } from '../contexts/ScopeContext';
import ScopeBar from '../components/ScopeBar';
import { computeComparisonRange } from '../utils/comparisonPeriods';
import { gapsNeedingAttention } from './overview/overviewApiGaps';
import OverviewReferralsTab from './overview/OverviewReferralsTab';
import OverviewSitesTab from './overview/OverviewSitesTab';
import OverviewUsersTab from './overview/OverviewUsersTab';
import OverviewImagingTab from './overview/OverviewImagingTab';
import OverviewTouchTab from './overview/OverviewTouchTab';
import {
  alignMonthlyYoY,
  buildAttentionSignals,
  buildInsightBlurb,
  countActiveUsers,
  countDormantUsers,
  countQuietReferringSites,
  daysAgoLabel,
  formatMoney,
  formatPctDelta,
  healthLabel,
  latestQbrDate,
  monthlyReferralSeries,
  normalizeCsRepDisplayNames,
  parseHealthScore,
  pctChange,
  sumMetric,
} from './overview/overviewCompute';
import './Overview.css';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'referrals', label: 'Referrals' },
  { id: 'sites', label: 'Referring Sites' },
  { id: 'users', label: 'Users' },
  { id: 'imaging', label: 'Imaging & Fax' },
  { id: 'touch', label: 'Touchpoints' },
  { id: 'reports', label: 'Reports' },
];

function KpiTile({ label, value, delta, sub }) {
  const d = delta === undefined ? null : formatPctDelta(delta);
  return (
    <div className="overview-kpi">
      <small>{label}</small>
      <b>{value}</b>
      {sub != null ? (
        <span className="overview-delta flat">{sub}</span>
      ) : d ? (
        <span className={`overview-delta ${d.kind}`}>{d.text}</span>
      ) : null}
    </div>
  );
}

function MonthlyBars({ series, currentYear, priorYear }) {
  const max = Math.max(1, ...series.flatMap(s => [s.current, s.prior]));
  return (
    <>
      <div className="overview-chart-legend">
        <span><i className="overview-sw" style={{ background: '#0f7a5c' }} />{currentYear}</span>
        <span><i className="overview-sw" style={{ background: '#cbd5e1' }} />{priorYear}</span>
      </div>
      <div className="overview-bars" role="img" aria-label="Referrals by month versus last year">
        {series.map(s => (
          <div key={s.label} className="overview-bar-group">
            <div className="overview-bar-pair">
              <div
                className="overview-bar current"
                style={{ height: `${(s.current / max) * 100}%` }}
                title={`${s.label} ${currentYear}: ${s.current}`}
              />
              <div
                className="overview-bar prior"
                style={{ height: `${(s.prior / max) * 100}%` }}
                title={`${s.label} ${priorYear}: ${s.prior}`}
              />
            </div>
            <span className="overview-bar-label">{s.label}</span>
          </div>
        ))}
      </div>
    </>
  );
}

function Overview() {
  const cache = useCache();
  const { customers, resolveCustomerFacilityIds } = useCustomerFilter();
  const { scope: globalScope, clearScope: clearGlobalScope } = useScope();

  const [selectedFacilityIds, setSelectedFacilityIds] = useState(new Set());
  const [activeCustomerId, setActiveCustomerId] = useState('');
  const [activeFilterId, setActiveFilterId] = useState('');
  const [savedFilters, setSavedFilters] = useState([]);

  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [customer, setCustomer] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [cacheBundle, setCacheBundle] = useState(null);

  const range = useMemo(() => computeComparisonRange('ytd_full_months'), []);

  const currentScope = useCurrentScope({
    activeCustomerId,
    activeFilterId,
    selectedFacilityIds,
    customers,
    savedFilters,
    setActiveCustomerId,
    setActiveFilterId,
    setSelectedFacilityIds,
    clearGlobalScope,
  });

  useEffect(() => {
    (async () => {
      try {
        const response = await cache.getSavedFilters();
        if (response?.filters) setSavedFilters(response.filters);
      } catch { /* optional */ }
    })();
  }, [cache]);

  const loadData = useCallback(async () => {
    if (!range.currentStart || !range.currentEnd) return;
    setLoading(true);
    setError('');
    try {
      // Resolve from global scope (not React state) — useScopeSync calls
      // loadData immediately after setState, before facility/customer ids commit.
      let custId = '';
      let facIds = new Set();
      if (globalScope?.kind === 'customer') {
        custId = String(globalScope.id);
        const ids = await resolveCustomerFacilityIds(globalScope.id);
        facIds = new Set(ids);
      } else if (globalScope?.kind === 'filter') {
        const sf = savedFilters.find(x => String(x.id) === String(globalScope.id));
        facIds = new Set(sf?.facilityIds || []);
      } else if (globalScope?.kind === 'facility') {
        facIds = new Set([String(globalScope.id)]);
      } else {
        facIds = selectedFacilityIds;
        custId = activeCustomerId;
      }

      const softCache = async (type, start, end) => {
        try {
          const res = await cache.getCachedData(type, start, end);
          return res?.records || [];
        } catch {
          return [];
        }
      };

      // referral-events: load unscoped (no date filter) so late consult/
      // procedure events still join to in-window referrals — same as
      // ReferralConversionReport.
      const [
        referralsCurrent, referralsPrior,
        loginsCurrent, loginsPrior,
        messagesCurrent, messagesPrior,
        studiesCurrent, studiesPrior,
        referralEvents,
        vitalroundsCurrent, vitalroundsPrior,
        faxesCurrent,
      ] = await Promise.all([
        softCache('referrals', range.currentStart, range.currentEnd),
        softCache('referrals', range.previousStart, range.previousEnd),
        softCache('logins', range.currentStart, range.currentEnd),
        softCache('logins', range.previousStart, range.previousEnd),
        softCache('messages', range.currentStart, range.currentEnd),
        softCache('messages', range.previousStart, range.previousEnd),
        softCache('studies', range.currentStart, range.currentEnd),
        softCache('studies', range.previousStart, range.previousEnd),
        softCache('referral-events', undefined, undefined),
        softCache('vitalrounds', range.currentStart, range.currentEnd),
        softCache('vitalrounds', range.previousStart, range.previousEnd),
        softCache('faxes', range.currentStart, range.currentEnd),
      ]);

      setCacheBundle({
        referralsCurrent,
        referralsPrior,
        loginsCurrent,
        loginsPrior,
        messagesCurrent,
        messagesPrior,
        studiesCurrent,
        studiesPrior,
        referralEvents,
        vitalroundsCurrent,
        vitalroundsPrior,
        faxesCurrent,
      });

      if (custId) {
        try {
          const [custRes, metRes] = await Promise.all([
            client.get(`/api/customers/${custId}`),
            client.get(
              `/api/customers/${custId}/metrics?currentStart=${range.currentStart}&currentEnd=${range.currentEnd}`
              + `&previousStart=${range.previousStart}&previousEnd=${range.previousEnd}`
            ),
          ]);
          setCustomer(custRes?.data?.customer || null);
          setMetrics(metRes?.data?.metrics || null);
        } catch (e) {
          setCustomer(null);
          setMetrics(null);
          setError(e.response?.data?.error || 'Could not load customer metrics');
        }
      } else {
        setCustomer(null);
        setMetrics(null);
      }

      // Keep local selection aligned if we resolved ahead of setState.
      if (facIds.size > 0) setSelectedFacilityIds(facIds);
      if (custId) setActiveCustomerId(custId);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to load overview');
      setCacheBundle(null);
    } finally {
      setLoading(false);
    }
  }, [
    cache, range, selectedFacilityIds, activeCustomerId,
    globalScope, savedFilters, resolveCustomerFacilityIds,
  ]);

  useScopeSync({
    customers,
    savedFilters,
    resolveCustomerFacilityIds,
    setSelectedFacilityIds,
    setActiveCustomerId,
    setActiveFilterId,
    loadData,
    ready: Boolean(range.currentStart && range.currentEnd),
  });

  const derived = useMemo(() => {
    if (!cacheBundle) return null;
    const facIds = selectedFacilityIds;

    let current = {};
    let previous = {};
    if (metrics?.current) {
      current = metrics.current;
      previous = metrics.previous || {};
    } else {
      current = {
        referrals: sumMetric(cacheBundle.referralsCurrent, 'referrals', facIds),
        logins: sumMetric(cacheBundle.loginsCurrent, 'logins', facIds),
        messages: sumMetric(cacheBundle.messagesCurrent, 'messages', facIds),
        studies: sumMetric(cacheBundle.studiesCurrent, 'studies', facIds),
      };
      previous = {
        referrals: sumMetric(cacheBundle.referralsPrior, 'referrals', facIds),
        logins: sumMetric(cacheBundle.loginsPrior, 'logins', facIds),
        messages: sumMetric(cacheBundle.messagesPrior, 'messages', facIds),
        studies: sumMetric(cacheBundle.studiesPrior, 'studies', facIds),
      };
    }

    const deltas = {
      referrals: pctChange(current.referrals, previous.referrals),
      logins: pctChange(current.logins, previous.logins),
      messages: pctChange(current.messages, previous.messages),
      studies: pctChange(current.studies, previous.studies),
    };

    const currSeries = monthlyReferralSeries(
      cacheBundle.referralsCurrent, facIds, range.currentStart, range.currentEnd
    );
    const priorSeries = monthlyReferralSeries(
      cacheBundle.referralsPrior, facIds, range.previousStart, range.previousEnd
    );
    const monthly = alignMonthlyYoY(currSeries, priorSeries);

    const activeUsers = countActiveUsers(cacheBundle.loginsCurrent, facIds);
    const allLogins = [...(cacheBundle.loginsCurrent || []), ...(cacheBundle.loginsPrior || [])];
    const allRefs = [...(cacheBundle.referralsCurrent || []), ...(cacheBundle.referralsPrior || [])];
    const asOf = range.currentEnd;
    const quietSites = countQuietReferringSites(allRefs, facIds, asOf, 60);
    const dormantUsers = countDormantUsers(allLogins, facIds, asOf, 60);
    const signals = buildAttentionSignals({ deltas, quietSites, dormantUsers });
    const blurb = buildInsightBlurb({ deltas, quietSites });

    return {
      current,
      previous,
      deltas,
      monthly,
      activeUsers,
      quietSites,
      dormantUsers,
      signals,
      blurb,
    };
  }, [cacheBundle, metrics, range, selectedFacilityIds]);

  const health = customer?.health || customer?.Health;
  const healthMeta = healthLabel(health?.color || health?.Color);
  const healthScore = parseHealthScore(health);
  const csrNames = normalizeCsRepDisplayNames(customer || {});
  const money = customer ? formatMoney(customer.paymentAmount, customer.paymentFrequency) : null;
  const lastTouchDay = customer ? latestQbrDate(customer) : null;
  const lastTouchLabel = daysAgoLabel(lastTouchDay);
  const facilityCount = selectedFacilityIds.size || currentScope.facilityCount || 0;
  const gaps = useMemo(() => gapsNeedingAttention(), []);

  const currentYear = range.currentStart.slice(0, 4);
  const priorYear = range.previousStart.slice(0, 4);
  const hasScope = currentScope.kind !== 'none' && selectedFacilityIds.size > 0;
  const tabIndex = Math.max(0, TABS.findIndex(t => t.id === activeTab)) + 1;
  const activeTabLabel = TABS.find(t => t.id === activeTab)?.label || 'Overview';

  // When scope is cleared (ScopeBar or sticky bar), drop loaded account state.
  useEffect(() => {
    if (currentScope.kind === 'none') {
      setCustomer(null);
      setMetrics(null);
      setCacheBundle(null);
    }
  }, [currentScope.kind]);

  return (
    <div className="overview-page">
      <ScopeBar scope={currentScope} />

      <div className="overview-ahead">
        <div className="who">
          <p className="overview-eyebrow">Tab {tabIndex} of 7 · {activeTabLabel}</p>
          <h1>{currentScope.kind !== 'none' ? currentScope.name : 'Select an account'}</h1>
          <div className="overview-meta">
            {facilityCount > 0 && (
              <span><b>{facilityCount}</b> facilit{facilityCount === 1 ? 'y' : 'ies'}</span>
            )}
            {csrNames.length > 0 && (
              <span>CSR <b>{csrNames.join(', ')}</b></span>
            )}
            {money && <span><b>{money}</b></span>}
          </div>
          <p className="overview-window">
            {range.currentLabel} vs {range.previousLabel}
          </p>
        </div>
        <div className="overview-pills">
          {lastTouchLabel && (
            <span className="overview-pill gray">{lastTouchLabel}</span>
          )}
          {health && (
            <span className={`overview-pill ${healthMeta.key}`}>
              {healthScore != null
                ? `Health ${healthScore} · ${healthMeta.label}`
                : `Health · ${healthMeta.label}`}
            </span>
          )}
        </div>
      </div>

      <div className="overview-tabs" role="tablist" aria-label="Account overview sections">
        {TABS.map(t => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={activeTab === t.id}
            className={`overview-tab ${activeTab === t.id ? 'on' : ''}`}
            onClick={() => setActiveTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && <div className="overview-error">{error}</div>}

      {!hasScope && !loading && activeTab !== 'reports' && (
        <div className="overview-empty">
          Pick a customer, filter, or facility from My Customers (or the analysis shortcuts)
          to load this account overview.
        </div>
      )}

      {activeTab === 'overview' && (
        <div className="overview-panel" role="tabpanel">
          {loading && <div className="overview-loading">Loading overview…</div>}

          {hasScope && !loading && derived && (
            <>
              <div className="overview-kpi-grid">
                <KpiTile
                  label="REFERRALS"
                  value={(derived.current.referrals ?? 0).toLocaleString()}
                  delta={derived.deltas.referrals}
                />
                <KpiTile
                  label="ACTIVE USERS"
                  value={derived.activeUsers.toLocaleString()}
                  sub="in period (no seat total)"
                />
                <KpiTile
                  label="LOGINS"
                  value={(derived.current.logins ?? 0).toLocaleString()}
                  delta={derived.deltas.logins}
                />
                <KpiTile
                  label="MESSAGES"
                  value={(derived.current.messages ?? 0).toLocaleString()}
                  delta={derived.deltas.messages}
                />
                <KpiTile
                  label="STUDIES"
                  value={(derived.current.studies ?? 0).toLocaleString()}
                  delta={derived.deltas.studies}
                />
              </div>

              <div className="overview-split">
                <div className="overview-box">
                  <h3>Referrals received by month</h3>
                  {derived.monthly.length === 0 ? (
                    <p className="overview-sub">No referral months in this window.</p>
                  ) : (
                    <MonthlyBars
                      series={derived.monthly}
                      currentYear={currentYear}
                      priorYear={priorYear}
                    />
                  )}
                </div>

                <div className="overview-box">
                  <h3>
                    Needs attention
                    <span className="overview-pill gray" style={{ fontSize: 12, padding: '3px 10px' }}>
                      {derived.signals.length || 'None'}
                    </span>
                  </h3>
                  {derived.signals.length === 0 ? (
                    <p className="overview-sub">Nothing flagged. Keep the cadence.</p>
                  ) : (
                    derived.signals.map(s => (
                      <div key={s.id} className="overview-signal">
                        <span className={`overview-sev ${s.severity}`} />
                        <span>{s.text}</span>
                      </div>
                    ))
                  )}
                  <div className="overview-vera">
                    <strong>Insight:</strong> {derived.blurb}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {activeTab === 'referrals' && hasScope && (
        <div role="tabpanel">
          <OverviewReferralsTab
            referralRecords={cacheBundle?.referralsCurrent || []}
            priorReferralRecords={cacheBundle?.referralsPrior || []}
            eventRecords={cacheBundle?.referralEvents || []}
            facIds={selectedFacilityIds}
            windowLabel={range.short || range.currentLabel}
            loading={loading}
          />
        </div>
      )}

      {activeTab === 'sites' && hasScope && (
        <div role="tabpanel">
          <OverviewSitesTab
            referralsCurrent={cacheBundle?.referralsCurrent || []}
            referralsPrior={cacheBundle?.referralsPrior || []}
            loginsCurrent={cacheBundle?.loginsCurrent || []}
            messagesCurrent={cacheBundle?.messagesCurrent || []}
            studiesCurrent={cacheBundle?.studiesCurrent || []}
            facIds={selectedFacilityIds}
            asOfDay={range.currentEnd}
            windowLabel={range.short || range.currentLabel}
            loading={loading}
          />
        </div>
      )}

      {activeTab === 'users' && hasScope && (
        <div role="tabpanel">
          <OverviewUsersTab
            loginsCurrent={cacheBundle?.loginsCurrent || []}
            loginsPrior={cacheBundle?.loginsPrior || []}
            messagesCurrent={cacheBundle?.messagesCurrent || []}
            referralsCurrent={cacheBundle?.referralsCurrent || []}
            facIds={selectedFacilityIds}
            asOfDay={range.currentEnd}
            loading={loading}
          />
        </div>
      )}

      {activeTab === 'imaging' && hasScope && (
        <div role="tabpanel">
          <OverviewImagingTab
            studiesCurrent={cacheBundle?.studiesCurrent || []}
            studiesPrior={cacheBundle?.studiesPrior || []}
            faxesCurrent={cacheBundle?.faxesCurrent || []}
            vitalroundsCurrent={cacheBundle?.vitalroundsCurrent || []}
            vitalroundsPrior={cacheBundle?.vitalroundsPrior || []}
            referralsCurrent={cacheBundle?.referralsCurrent || []}
            facIds={selectedFacilityIds}
            metrics={metrics}
            loading={loading}
          />
        </div>
      )}

      {activeTab === 'touch' && hasScope && (
        <div role="tabpanel">
          <OverviewTouchTab
            customer={customer}
            loading={loading}
            hasCustomerScope={currentScope.kind === 'customer'}
          />
        </div>
      )}

      {activeTab === 'reports' && (
        <div className="overview-empty" role="tabpanel">
          <strong>Reports</strong> is not built yet — use Agentic Reporting for QBR-style outputs.
        </div>
      )}

      <details className="overview-gaps">
        <summary>
          Data gaps ({gaps.length}) — elements without full API support
        </summary>
        <div className="overview-gaps-body">
          <table className="overview-gaps-table">
            <thead>
              <tr>
                <th>Status</th>
                <th>Tab</th>
                <th>Element</th>
                <th>v1 behavior</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {gaps.map(g => (
                <tr key={g.id}>
                  <td>
                    <span className={`overview-gap-status ${g.status}`}>
                      {g.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td>{g.tab}</td>
                  <td>{g.element}</td>
                  <td>{g.v1Behavior}</td>
                  <td>{g.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
}

export default Overview;
